import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { CustomerAddress, CustomerObject } from 'containers/customers/customers.model';
import {
  AssignableMember,
  AutoFillJobDetailResponse,
  Job,
  JobAttachmentType,
  JobCustomer,
  JobExternalCompany,
  JobLeadSourceType,
  ServiceAreaInfo
} from '../jobs.model';
import { RootState } from 'store/reducers';
import {
  EInvoiceCatalogElementType,
  InvoiceDraftModalData,
  InvoiceGlobalTaxGroups,
  InvoiceValidationError
} from 'containers/invoices/create-invoice/types';
import { AbstractITax, ITax } from 'containers/product-catalog/taxes/taxes.model';
import {
  InvoiceLineItem,
  InvoiceSurchargeState,
  TaxesAndFeesPayloadState,
  calculateFinalAmount,
  calculateSummatedLineItemLevelTaxesCurrency,
  calculateSurchargeInCurrency,
  calculateSurchargeInCurrencyWithBps,
  calculateSurchargeWithBpsPercentage,
  getLineItemsTotal,
  hasServiceCharge,
  updateServiceChargeWithBaseAmount
} from 'containers/invoices/InvoicesSlice';
import { FeatureToggleState } from 'containers/settings/constants';
import { initServiceCharge } from 'containers/invoices/create-invoice/constants';
import { findAppliedUniqueTaxes } from 'containers/invoices/edit-invoice/invoicesEditSlice';
import { initialDiscountFormData } from 'containers/invoices/types';
import {
  AdditionalTax,
  Attachment,
  Customer,
  DiscountSettings,
  IInvoiceServiceCharge,
  IInvoiceServiceChargeFromResponse,
  InvoiceByIdResponseV2
} from 'containers/invoices/invoices.model';
import { DiscountFormData, LineItemAdditionalTax } from 'containers/invoices/types';
import {
  calculateInvoiceSubtotalAndNewDiscount,
  calculateLineItemAdditionalTaxInCurrency,
  calculateTotalOriginalUnitCost,
  isSameCatalogLineItem,
  prepareInvoiceAttachments,
  prepareInvoiceCustomerFromV2Response,
  prepareInvoiceDiscountFromV2Response,
  prepareInvoiceLineItemsFromV2Response,
  prepareInvoiceServiceChargeFromV2Response
} from 'containers/invoices/utils';
import { GlobalServiceChargeTax } from 'containers/product-catalog/catalog.model';
import { AddressField, CustomerDetails } from 'containers/invoices/invoices.model';
import { CreateJobErrorTypes, LeadSourceOptions } from '../constant';
import { parseLineItemTaxesFromResponse, prepareInvoiceServiceCharge } from 'containers/invoices/utils';
import { DISCOUNT_TYPE } from 'constants/common';
import { Estimate } from 'containers/estimates/estimates.model';
import { EstimateStatus } from 'containers/estimates/constants';
import { Tag } from 'containers/tags/tags.model';
import { UploadAttachmentFile } from 'types/baseTypes';

interface CreateJobSliceState {
  serviceAddress: CustomerAddress | AddressField;
  initialServiceAddress: CustomerAddress | AddressField;
  jobCustomer: CustomerObject | CustomerDetails | JobCustomer | Customer;
  initialJobCustomer: CustomerObject | CustomerDetails | JobCustomer | Customer;
  billingAddress: CustomerAddress | AddressField;
  initialBillingAddress: CustomerAddress | AddressField;
  jobNotes: string;
  jobAttachments: JobAttachmentType[];
  initialJobReferenceNumber: string;
  jobReferenceNumber: string;
  assigneeList: AssignableMember[];
  initialAssigneeList: AssignableMember[];
  serviceAreaAssignees: AssignableMember[];
  assignableTeamMembers: AssignableMember[];
  selectedDate: string;
  scheduledFrom: string;
  scheduledTo: string;
  isBackFromCreateTaxRoute: boolean;
  jobDraftModalData?: InvoiceDraftModalData;
  jobValidationErrors: InvoiceValidationError[];
  currentSurcharge: InvoiceSurchargeState;
  initialSurcharge: InvoiceSurchargeState;
  finalAmount: number; // final amount = subtotal + service fee + service charge + sum of line item level taxes
  appliedLineItemLevelTaxes: AdditionalTax[];
  selectedServiceCharge: IInvoiceServiceCharge;
  initialServiceCharge: IInvoiceServiceCharge;
  isServiceFeeEnabled: boolean;
  isServiceFeeEnabledInitial: boolean;
  lineItemsTotal: number;
  jobSubTotal: number;
  initialSubTotal: number;
  lineItemsStateCount: number;
  discountSettings: DiscountSettings;
  discountFormData: DiscountFormData;
  jobLineItems: InvoiceLineItem[];
  initialJobLineItems: InvoiceLineItem[];
  globalLineItemTaxes: ITax[];
  globalServiceChargeTaxes: GlobalServiceChargeTax[];
  jobDescription: string;
  jobTitle: string;
  leadSource: string;
  leadSourceType: JobLeadSourceType;
  isJobScheduled: boolean;
  isInitialisingSliceFromAPI: boolean;
  isJobFromInvoice: boolean;
  invoiceNumberForJobFromInvoice: string;
  invoiceStatus?: string;
  isFromEstimate: boolean;
  estimateNumber: string;
  estimateId: string;
  estimateStatus: EstimateStatus;
  jobRelatedEntityData: {
    estimateId?: string;
    invoiceId?: string;
  };
  externalCompany: JobExternalCompany | null;
  autoFilledData: AutoFillJobDetailResponse['data'];
  totalOriginalUnitCost: number;
  initialServiceArea: ServiceAreaInfo | null;
  serviceArea: ServiceAreaInfo | null;
  autofillKey?: string;
  selectedTags: Tag[];
  jobAutoFilledCount: number;
}

const initialSurchargeState: InvoiceSurchargeState = {
  enabled: false,
  basePoints: null,
  percentage: null,
  percentageWithBps: null,
  valueInCurrency: null,
  valueInCurrencyWithBps: null,
  config: null
};

const initialState: CreateJobSliceState = {
  serviceAddress: null,
  initialServiceAddress: null,
  jobCustomer: null,
  initialJobCustomer: null,
  billingAddress: null,
  initialBillingAddress: null,
  jobNotes: '',
  jobAttachments: [],
  initialJobReferenceNumber: '',
  jobReferenceNumber: '',
  assigneeList: [],
  initialAssigneeList: [],
  serviceAreaAssignees: [],
  assignableTeamMembers: [],
  selectedDate: new Date().toISOString(),
  scheduledFrom: new Date().toISOString(),
  scheduledTo: new Date(new Date().getTime() + 60 * 60 * 1000).toISOString(),
  isBackFromCreateTaxRoute: false,
  jobDraftModalData: null,
  jobValidationErrors: [],
  currentSurcharge: initialSurchargeState,
  initialSurcharge: initialSurchargeState,
  lineItemsTotal: 0,
  jobSubTotal: 0,
  initialSubTotal: 0,
  lineItemsStateCount: 0,
  discountSettings: { enabled: true },
  discountFormData: initialDiscountFormData,
  finalAmount: 0,
  appliedLineItemLevelTaxes: [],
  selectedServiceCharge: initServiceCharge,
  initialServiceCharge: initServiceCharge,
  isServiceFeeEnabled: false,
  isServiceFeeEnabledInitial: false,
  jobLineItems: [],
  initialJobLineItems: [],
  globalLineItemTaxes: [],
  globalServiceChargeTaxes: [],
  jobDescription: '',
  jobTitle: '',
  leadSource: '',
  leadSourceType: null,
  isJobScheduled: true,
  isInitialisingSliceFromAPI: false,
  isJobFromInvoice: false,
  invoiceNumberForJobFromInvoice: '',
  invoiceStatus: '',
  isFromEstimate: false,
  estimateId: '',
  estimateNumber: '',
  estimateStatus: null,
  jobRelatedEntityData: {
    invoiceId: '',
    estimateId: ''
  },
  externalCompany: null,
  autoFilledData: null,
  totalOriginalUnitCost: 0,
  initialServiceArea: null,
  serviceArea: null,
  autofillKey: '',
  selectedTags: [],
  jobAutoFilledCount: 0
};

export const createJobSlice = createSlice({
  name: 'createJob',
  initialState,
  reducers: {
    resetCreateJobState: () => {
      // Generate fresh dates each time the state is reset
      const currentDateTime = new Date();
      const oneHourLaterDateTime = new Date(currentDateTime.getTime() + 60 * 60 * 1000).toISOString();

      return {
        ...initialState,
        selectedDate: currentDateTime.toISOString(),
        scheduledFrom: currentDateTime.toISOString(),
        scheduledTo: oneHourLaterDateTime
      };
    },

    updateServiceAddress: (state, { payload }: PayloadAction<CustomerAddress | AddressField>) => {
      state.serviceAddress = payload;
    },
    updateBillingAddress: (state, { payload }: PayloadAction<CustomerAddress>) => {
      state.billingAddress = payload;
    },
    updateJobCustomer: (state, { payload }: PayloadAction<CustomerObject | CustomerDetails>) => {
      state.jobCustomer = payload;
    },
    updateJobNotes: (state, { payload }: PayloadAction<string>) => {
      state.jobNotes = payload;
    },
    updateJobAttachments: (state, { payload }: PayloadAction<JobAttachmentType>) => {
      const id = state.jobAttachments.length + 1;
      const updatedAttachments = [...state.jobAttachments, { ...payload, id: id.toString() }];
      state.jobAttachments = updatedAttachments;
    },
    removeJobAttachments: (state, { payload }: PayloadAction<JobAttachmentType>) => {
      const updatedAttachments = state.jobAttachments.filter(attachment => attachment.url !== payload.url);
      state.jobAttachments = updatedAttachments;
    },
    modifyJobAttachment: (state, { payload }: PayloadAction<JobAttachmentType>) => {
      const updatedAttachmentList = state.jobAttachments.map(attachment => {
        if (attachment.id === payload.id) {
          return {
            ...attachment,
            url: payload.url,
            title: payload.title,
            imagePreview: payload.imagePreview
          };
        }
        return attachment;
      });
      state.jobAttachments = updatedAttachmentList;
    },
    updateMultipleJobAttachments: (state, { payload }: PayloadAction<UploadAttachmentFile[]>) => {
      let idCounter = state.jobAttachments.length + 1;
      const newAttachments: JobAttachmentType[] = payload.map(p => {
        const id = p.id ? p.id.toString() : (idCounter++).toString();
        return {
          id,
          url: p.url,
          title: p.title,
          imagePreview: p.previewData || p.urlLink,
          urlLink: p.urlLink,
          source: p.source || 'JOB'
        };
      });
      state.jobAttachments = [...state.jobAttachments, ...newAttachments];
    },
    resetJobAttachmentState: state => {
      state.jobAttachments = [];
    },
    updateJobReferenceNumber: (state, { payload }: PayloadAction<string>) => {
      state.jobReferenceNumber = payload;
    },
    updateAssigneeList: (state, { payload }: PayloadAction<AssignableMember[]>) => {
      state.assigneeList = payload;
    },
    updateSelectedDate: (state, { payload }: PayloadAction<string>) => {
      state.selectedDate = payload;
    },
    updateScheduledFrom: (state, { payload }: PayloadAction<string>) => {
      state.scheduledFrom = payload;
    },
    updateScheduledTo: (state, { payload }: PayloadAction<string>) => {
      state.scheduledTo = payload;
    },
    updateIsBackFromCreateTaxInJobsSlice: (state, { payload }: PayloadAction<boolean>) => {
      state.isBackFromCreateTaxRoute = payload;
    },
    updateJobDraftData: (state, { payload }: PayloadAction<InvoiceDraftModalData>) => {
      state.jobDraftModalData = payload;
    },

    resetJobDraftData: state => {
      state.jobDraftModalData = null;
    },
    addCreatedTaxInInvoiceDraft: (state, { payload }: PayloadAction<AbstractITax>) => {
      if (state?.jobDraftModalData?.modalConfig) {
        const currentModalConfig = state?.jobDraftModalData?.modalConfig ?? {};
        const currentModalConfigData = currentModalConfig?.data ?? {};
        if (state.jobDraftModalData.invoiceCatalogElementType === EInvoiceCatalogElementType.ITEM) {
          const currentlySelectedTaxes = currentModalConfigData?.selectedTaxes ?? [];
          state.jobDraftModalData = {
            ...state.jobDraftModalData,
            modalConfig: {
              ...currentModalConfig,
              data: { ...currentModalConfigData, selectedTaxes: [...currentlySelectedTaxes, payload] }
            }
          };
        } else {
          // for service charge draft
          const currentlySelectedTaxes = currentModalConfigData?.taxes ?? [];
          state.jobDraftModalData = {
            ...state.jobDraftModalData,
            modalConfig: {
              ...currentModalConfig,
              data: { ...currentModalConfigData, taxes: [...currentlySelectedTaxes, payload] }
            }
          };
        }
      }
    },
    addJobValidationError: (state, { payload }: PayloadAction<InvoiceValidationError>) => {
      state.jobValidationErrors = [...state.jobValidationErrors, payload];
    },

    removeJobValidationErrors: (state, { payload }: PayloadAction<CreateJobErrorTypes>) => {
      state.jobValidationErrors = state.jobValidationErrors.filter(error => error.type !== payload);
    },

    resetJobValidationErrors: state => {
      state.jobValidationErrors = [];
    },
    addJobLineItem: (state, { payload }: PayloadAction<InvoiceLineItem>) => {
      const updatedLineItemsList = [
        ...state.jobLineItems,
        {
          ...payload,
          id: state.lineItemsStateCount.toString(),
          total: +payload.unitCost * +payload.quantity
        }
      ];
      state.lineItemsStateCount = state.lineItemsStateCount + 1;
      state.jobLineItems = updatedLineItemsList;

      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(updatedLineItemsList);

      const totalCostOfLineItems = getLineItemsTotal(updatedLineItemsList);
      state.lineItemsTotal = totalCostOfLineItems;
      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: totalCostOfLineItems,
        discountData: state.discountFormData
      }) ?? { subtotal: totalCostOfLineItems, discountData: state.discountFormData };

      // Update subtotal state
      state.jobSubTotal = subtotal;
      // update discount data
      state.discountFormData = newDiscountData;

      // Update surcharge state

      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(state.currentSurcharge.percentage, subtotal);
        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurcharge.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.currentSurcharge = {
          percentage: state.currentSurcharge.percentage,
          valueInCurrency: surchargeInCurrency,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps,
          enabled: state.currentSurcharge.enabled,
          basePoints: state.currentSurcharge.basePoints,
          config: state.currentSurcharge.config
        };
      }

      // update applied line item taxes array
      const itemLevelTaxesInPayload = payload?.taxes ?? [];
      let modifiedAppliedLineItemLevelTaxes: AdditionalTax[] = state.appliedLineItemLevelTaxes;
      if (itemLevelTaxesInPayload.length > 0) {
        itemLevelTaxesInPayload.forEach(itemLevelTaxInPayload => {
          const isLineItemTaxAlreadySelected = modifiedAppliedLineItemLevelTaxes.find(
            tax => tax.id === itemLevelTaxInPayload.id
          );

          if (!isLineItemTaxAlreadySelected) {
            modifiedAppliedLineItemLevelTaxes = [
              ...modifiedAppliedLineItemLevelTaxes,
              itemLevelTaxInPayload as AdditionalTax
            ];
          }
        });
      }
      state.appliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes;

      // calculate service charge base amount
      let updatedServiceCharge = state.selectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.currentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: updatedLineItemsList,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmount = finalAmount;

      if (state.jobLineItems.length === 0) {
        state.discountFormData = initialDiscountFormData;
      }
    },
    modifyJobLineItem: (state, { payload }: PayloadAction<InvoiceLineItem>) => {
      let lineItemToUpdate: InvoiceLineItem = null;
      const updatedLineItemsList = state.jobLineItems.map(lineItem => {
        if (lineItem.id === payload.id) {
          lineItemToUpdate = lineItem;
          return {
            ...lineItem,
            itemDescription: payload.itemDescription,
            unitCost: payload.unitCost,
            originalUnitCost: payload.originalUnitCost,
            markupPercentage: payload.markupPercentage,
            quantity: payload.quantity,
            total: +payload.unitCost * +payload.quantity,
            additionalDetails: payload.additionalDetails,
            ...(payload?.taxes ? { taxes: payload.taxes } : { taxes: [] })
          };
        }
        return lineItem;
      });

      state.jobLineItems = updatedLineItemsList;

      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(updatedLineItemsList);

      const totalCostOfLineItems = getLineItemsTotal(updatedLineItemsList);
      state.lineItemsTotal = totalCostOfLineItems;

      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: totalCostOfLineItems,
        discountData: state.discountFormData
      }) ?? { subtotal: totalCostOfLineItems, discountData: state.discountFormData };
      // Update subtotal state
      state.jobSubTotal = subtotal;
      //  Update discount data
      state.discountFormData = newDiscountData;

      // Update surcharge state
      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(state.currentSurcharge.percentage, subtotal);

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurcharge.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);
        state.currentSurcharge = {
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps,
          percentage: state.currentSurcharge.percentage,
          valueInCurrency: surchargeInCurrency,
          config: state.currentSurcharge.config,
          enabled: state.currentSurcharge.enabled,
          basePoints: state.currentSurcharge.basePoints
        };
      }

      // calculate service charge base amount
      let updatedServiceCharge = state.selectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
      }

      // update applied line item taxes array - currently selected line item level taxes
      const itemLevelTaxesInPayload = payload?.taxes ?? [];
      const taxesInItemBeforeUpdate = lineItemToUpdate?.taxes ?? [];

      if (itemLevelTaxesInPayload.length > 0) {
        itemLevelTaxesInPayload.forEach(itemLevelTaxInPayload => {
          const isLineItemTaxAlreadySelected = state.appliedLineItemLevelTaxes.find(
            tax => tax.id === itemLevelTaxInPayload.id
          );
          if (!isLineItemTaxAlreadySelected) {
            state.appliedLineItemLevelTaxes = [
              ...state.appliedLineItemLevelTaxes,
              itemLevelTaxInPayload as AdditionalTax
            ];
          }
        });
      }

      //    remove the current line item's item level tax from applied item level taxes array
      //    if this tax is not applied on any other line items

      if (taxesInItemBeforeUpdate.length > 0) {
        taxesInItemBeforeUpdate.forEach(taxInItemBeforeUpdate => {
          const isTaxAppliedOnOtherItem = updatedLineItemsList.find(lineItem => {
            return lineItem?.taxes?.find(lineItemTax => lineItemTax.id === taxInItemBeforeUpdate.id);
          });
          const isTaxAppliedOnServiceCharge = state.selectedServiceCharge?.taxes?.find(
            tax => tax.id === taxInItemBeforeUpdate.id
          );
          if (!isTaxAppliedOnOtherItem && !isTaxAppliedOnServiceCharge) {
            state.appliedLineItemLevelTaxes = state.appliedLineItemLevelTaxes.filter(
              tax => tax.id !== taxInItemBeforeUpdate.id
            );
          }
        });
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.currentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: updatedLineItemsList,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmount = finalAmount;
    },

    incrementJobLineItemQty: (state, { payload }: PayloadAction<InvoiceLineItem>) => {
      const updatedLineItemsList = state.jobLineItems.map(lineItem => {
        if (isSameCatalogLineItem({ primaryItem: payload, secondaryItem: lineItem })) {
          const newQty = (+lineItem.quantity + 1).toString();
          const totalLineItemCost = +newQty * +lineItem.unitCost;
          let lineItemAdditionalTaxesWithAmount: LineItemAdditionalTax[] = null;
          const lineItemTaxes: LineItemAdditionalTax[] = lineItem?.taxes ?? [];
          if (lineItemTaxes.length > 0) {
            lineItemAdditionalTaxesWithAmount = lineItemTaxes.map(tax => {
              return {
                ...tax,
                amount: calculateLineItemAdditionalTaxInCurrency(tax.percentage, totalLineItemCost)
              };
            });
          }

          return {
            ...lineItem,
            quantity: newQty,
            total: totalLineItemCost,
            ...(lineItemAdditionalTaxesWithAmount ? { taxes: lineItemAdditionalTaxesWithAmount } : {})
          };
        }
        return lineItem;
      });

      state.jobLineItems = updatedLineItemsList;
      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(updatedLineItemsList);
      const totalCostOfLineItems = getLineItemsTotal(updatedLineItemsList);
      state.lineItemsTotal = totalCostOfLineItems;

      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: totalCostOfLineItems,
        discountData: state.discountFormData
      }) ?? { subtotal: totalCostOfLineItems, discountData: state.discountFormData };
      // Update subtotal state
      state.jobSubTotal = subtotal;
      //  Update discount data
      state.discountFormData = newDiscountData;

      // Update surcharge state
      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(state.currentSurcharge.percentage, subtotal);

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurcharge.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);
        state.currentSurcharge = {
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps,
          percentage: state.currentSurcharge.percentage,
          valueInCurrency: surchargeInCurrency,
          config: state.currentSurcharge.config,
          enabled: state.currentSurcharge.enabled,
          basePoints: state.currentSurcharge.basePoints
        };
      }

      // calculate service charge base amount
      let updatedServiceCharge = state.selectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.currentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: updatedLineItemsList,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmount = finalAmount;
    },

    removeJobLineItem: (state, { payload }: PayloadAction<InvoiceLineItem>) => {
      const updatedLineItemsList = state.jobLineItems.filter(lineItem => lineItem.id !== payload.id);

      if (updatedLineItemsList.length > 0) {
        state.jobLineItems = updatedLineItemsList;
        const totalCostOfLineItems = getLineItemsTotal(updatedLineItemsList);
        state.lineItemsTotal = totalCostOfLineItems;
        state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(updatedLineItemsList);

        const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
          totalCostOfItems: totalCostOfLineItems,
          discountData: state.discountFormData
        }) ?? { subtotal: totalCostOfLineItems, discountData: state.discountFormData };
        // Update subtotal state
        state.jobSubTotal = subtotal;
        //  Update discount data
        state.discountFormData = newDiscountData;

        // Update surcharge state
        let surchargeInCurrencyWithBps = 0;
        if (state.isServiceFeeEnabled) {
          const surchargeInCurrency: number = calculateSurchargeInCurrency(state.currentSurcharge.percentage, subtotal);
          surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
            surchargeInCurrency,
            state.currentSurcharge.basePoints
          );

          const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

          state.currentSurcharge = {
            percentageWithBps: surchargeWithBpsPercentage,
            valueInCurrencyWithBps: surchargeInCurrencyWithBps,
            percentage: state.currentSurcharge.percentage,
            valueInCurrency: surchargeInCurrency,
            config: state.currentSurcharge.config,
            enabled: state.currentSurcharge.enabled,
            basePoints: state.currentSurcharge.basePoints
          };
        }
        // calculate service charge base amount
        let updatedServiceCharge = state.selectedServiceCharge;
        let serviceChargeBaseAmount = 0;
        if (hasServiceCharge(updatedServiceCharge)) {
          const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
            updateServiceChargeWithBaseAmount({
              serviceCharge: updatedServiceCharge,
              subtotal: subtotal
            });
          serviceChargeBaseAmount = baseAmount;
          updatedServiceCharge = updatedServiceChargeWithBaseAmount;
          state.selectedServiceCharge = updatedServiceCharge;
        }

        // update applied line item taxes array
        const singleItemLevelTaxesToRemove: LineItemAdditionalTax[] = payload?.taxes ?? [];
        if (singleItemLevelTaxesToRemove.length > 0) {
          singleItemLevelTaxesToRemove.forEach(itemLevelTaxOnCurrentItem => {
            const isTaxToRemoveAppliedOnOtherItem = updatedLineItemsList.find(lineItem => {
              return lineItem?.taxes?.find(tax => tax?.id === itemLevelTaxOnCurrentItem?.id);
            });

            const isTaxAppliedOnServiceCharge = state.selectedServiceCharge?.taxes?.find(
              tax => tax.id === itemLevelTaxOnCurrentItem.id
            );

            if (!isTaxToRemoveAppliedOnOtherItem && !isTaxAppliedOnServiceCharge) {
              state.appliedLineItemLevelTaxes = state.appliedLineItemLevelTaxes.filter(
                tax => tax.id !== itemLevelTaxOnCurrentItem.id
              );
            }
          });
        }

        // Update final amount

        const surchargeInCurrencyBpsValue = state.currentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

        // update sum of line item level taxes
        const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
          lineItems: updatedLineItemsList,
          selectedServiceCharge: updatedServiceCharge
        });

        const finalAmount = calculateFinalAmount({
          subtotal,
          totalServiceChargeInCurrency: serviceChargeBaseAmount,
          surcharge: surchargeInCurrencyBpsValue,
          summatedLineItemLevelTaxesCurrency
        });
        state.finalAmount = finalAmount;
      } else {
        state.jobLineItems = [];
        state.lineItemsTotal = null;
        state.jobSubTotal = null;
        state.totalOriginalUnitCost = 0;
        state.currentSurcharge = {
          ...state.currentSurcharge,
          percentageWithBps: null,
          valueInCurrency: null,
          valueInCurrencyWithBps: null
        };
        state.selectedServiceCharge = initServiceCharge;
        state.appliedLineItemLevelTaxes = [];
        state.discountFormData = initialDiscountFormData;
        state.finalAmount = 0;
      }
    },

    updateJobDiscountFormData: (state, { payload }: PayloadAction<DiscountFormData>) => {
      state.discountFormData = payload;

      const subtotal = state.lineItemsTotal - +payload.discountInCurrency;

      // Update subtotal state
      state.jobSubTotal = subtotal;

      // Update surcharge state
      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(state.currentSurcharge.percentage, subtotal);

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurcharge.basePoints
        );
        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.currentSurcharge = {
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps,
          percentage: state.currentSurcharge.percentage,
          valueInCurrency: surchargeInCurrency,
          config: state.currentSurcharge.config,
          enabled: state.currentSurcharge.enabled,
          basePoints: state.currentSurcharge.basePoints
        };
      }

      // calculate service charge base amount
      let updatedServiceCharge = state.selectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.currentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.jobLineItems,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        surcharge: surchargeInCurrencyBpsValue,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmount = finalAmount;
    },

    removeJobDiscountFormData: state => {
      state.discountFormData = initialDiscountFormData;

      const subtotal = state.lineItemsTotal;

      // Update subtotal state
      state.jobSubTotal = subtotal;

      // Update surcharge state - re-calculate surcharge in currency
      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(state.currentSurcharge.percentage, subtotal);

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurcharge.basePoints
        );
        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);
        state.currentSurcharge = {
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps,
          percentage: state.currentSurcharge.percentage,
          valueInCurrency: surchargeInCurrency,
          config: state.currentSurcharge.config,
          enabled: state.currentSurcharge.enabled,
          basePoints: state.currentSurcharge.basePoints
        };
      }
      // calculate service charge base amount
      let updatedServiceCharge = state.selectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.currentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.jobLineItems,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmount = finalAmount;
    },

    // This action is dispatched after the taxes and fees api call
    updateInvoiceFeeSettingsInJobs: (state, { payload }: PayloadAction<TaxesAndFeesPayloadState>) => {
      if (payload.surchargeState.config === FeatureToggleState.OFF_BY_DEFAULT || !payload.surchargeState.enabled) {
        state.isServiceFeeEnabled = false;
      } else state.isServiceFeeEnabled = true;

      const surchargeInCurrency: number = calculateSurchargeInCurrency(
        payload.surchargeState.percentage,
        state.jobSubTotal
      );

      const surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
        surchargeInCurrency,
        state.currentSurcharge.basePoints
      );

      const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(
        surchargeInCurrencyWithBps,
        state.jobSubTotal
      );

      state.currentSurcharge = {
        percentageWithBps: surchargeWithBpsPercentage,
        valueInCurrencyWithBps: surchargeInCurrencyWithBps,
        percentage: payload.surchargeState.percentage,
        valueInCurrency: surchargeInCurrency,
        config: payload.surchargeState.config,
        enabled: payload.surchargeState.enabled,
        basePoints: payload.surchargeState.basePoints
      };

      state.discountSettings = payload.discountSettings;

      // calculate service charge base amount
      let updatedServiceCharge = state.selectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: state.jobSubTotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
      }

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.jobLineItems,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal: state.jobSubTotal,
        surcharge: state.isServiceFeeEnabled ? surchargeInCurrencyWithBps : 0,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmount = finalAmount;
    },

    // todo: modify final amount calc
    updateSelectedJobServiceCharge: (state, { payload }: PayloadAction<IInvoiceServiceCharge>) => {
      const currentServiceCharge: IInvoiceServiceCharge = { ...state.selectedServiceCharge };
      const newServiceCharge = payload;
      state.selectedServiceCharge = newServiceCharge;

      // Update final amount

      // calculate service charge base amount
      let updatedServiceCharge = newServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: state.jobSubTotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
      }

      const taxesInNewServiceCharge: AbstractITax[] = newServiceCharge?.taxes ?? [];
      const taxesInCurrentServiceCharge: AbstractITax[] = currentServiceCharge?.taxes ?? [];

      //  add taxes which are in the new service charge if they aren't already present in any of the line items
      if (
        (currentServiceCharge === initServiceCharge ||
          currentServiceCharge === null ||
          taxesInCurrentServiceCharge.length === 0) &&
        taxesInNewServiceCharge.length > 0
      ) {
        taxesInNewServiceCharge.forEach(serviceChargeTax => {
          const isLineItemTaxAlreadySelected = state.appliedLineItemLevelTaxes.find(
            tax => tax.id === serviceChargeTax.id
          );
          if (!isLineItemTaxAlreadySelected) {
            state.appliedLineItemLevelTaxes = [...state.appliedLineItemLevelTaxes, serviceChargeTax as AdditionalTax];
          }
        });
      } else {
        if (taxesInCurrentServiceCharge.length > 0 && taxesInNewServiceCharge.length > 0) {
          // Remove the taxes which were added because of the current service charge
          let modifiedAppliedLineItemLevelTaxes: AdditionalTax[] = [...state.appliedLineItemLevelTaxes];
          taxesInCurrentServiceCharge.forEach(serviceChargeTax => {
            const isTaxAppliedOnAnyLineItem = state.jobLineItems.find(lineItem => {
              return lineItem?.taxes?.find(tax => tax.id === serviceChargeTax.id);
            });
            if (!isTaxAppliedOnAnyLineItem) {
              modifiedAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes.filter(
                tax => tax.id !== serviceChargeTax.id
              );
            }
          });

          //  add the taxes which are in the new service charge
          taxesInNewServiceCharge.forEach(serviceChargeTax => {
            const isLineItemTaxAlreadySelected = modifiedAppliedLineItemLevelTaxes.find(
              tax => tax.id === serviceChargeTax.id
            );
            if (!isLineItemTaxAlreadySelected) {
              modifiedAppliedLineItemLevelTaxes = [
                ...modifiedAppliedLineItemLevelTaxes,
                serviceChargeTax as AdditionalTax
              ];
            }
          });
          state.appliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes;
        } else if (taxesInCurrentServiceCharge.length > 0 && taxesInNewServiceCharge.length === 0) {
          // Remove the taxes which were added because of the current service charge
          let modifiedAppliedLineItemLevelTaxes: AdditionalTax[] = [...state.appliedLineItemLevelTaxes];
          taxesInCurrentServiceCharge.forEach(serviceChargeTax => {
            const isTaxAppliedOnAnyLineItem = state.jobLineItems.find(lineItem => {
              return lineItem?.taxes?.find(tax => tax.id === serviceChargeTax.id);
            });
            if (!isTaxAppliedOnAnyLineItem) {
              modifiedAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes.filter(
                tax => tax.id !== serviceChargeTax.id
              );
            }
          });
          state.appliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes;
        }
      }

      const surchargeInCurrencyBpsValue = state.isServiceFeeEnabled ? state.currentSurcharge.valueInCurrencyWithBps : 0;

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.jobLineItems,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal: state.jobSubTotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmount = finalAmount;
    },

    removeJobServiceCharge: state => {
      const currentSelectedServiceCharge = { ...state.selectedServiceCharge };
      state.selectedServiceCharge = initServiceCharge;

      // Update final amount
      const surchargeInCurrencyBpsValue = state.isServiceFeeEnabled ? state.currentSurcharge.valueInCurrencyWithBps : 0;

      // update applied line item taxes array
      const serviceChargeTaxesToRemove: LineItemAdditionalTax[] = currentSelectedServiceCharge?.taxes ?? [];
      let modifiedAppliedLineItemLevelTaxes: AdditionalTax[] = [...state.appliedLineItemLevelTaxes];
      if (serviceChargeTaxesToRemove.length > 0) {
        serviceChargeTaxesToRemove.forEach(taxInCurrentServiceCharge => {
          const isTaxToRemoveAppliedOnAnyLineItem: InvoiceLineItem = state.jobLineItems.find(lineItem => {
            return lineItem?.taxes?.find(tax => tax?.id === taxInCurrentServiceCharge?.id);
          });
          if (!isTaxToRemoveAppliedOnAnyLineItem) {
            modifiedAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes.filter(
              tax => tax.id !== taxInCurrentServiceCharge.id
            );
          }
        });
        state.appliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes;
      }

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.jobLineItems
      });

      const finalAmount = calculateFinalAmount({
        subtotal: state.jobSubTotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: 0,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmount = finalAmount;
    },
    updateIsServiceFeeEnabledInJobs: (state, { payload }) => {
      state.isServiceFeeEnabled = payload;
      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.currentSurcharge.percentage,
          state.jobSubTotal
        );

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurcharge.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(
          surchargeInCurrencyWithBps,
          state.jobSubTotal
        );

        state.currentSurcharge = {
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps,
          percentage: state.currentSurcharge.percentage,
          valueInCurrency: surchargeInCurrency,
          config: state.currentSurcharge.config,
          enabled: state.currentSurcharge.enabled,
          basePoints: state.currentSurcharge.basePoints
        };
      }
      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.jobLineItems
      });

      const finalAmount = calculateFinalAmount({
        subtotal: state.jobSubTotal,
        surcharge: surchargeInCurrencyWithBps,
        totalServiceChargeInCurrency: +state.selectedServiceCharge.totalAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmount = finalAmount;
    },
    addGlobalTaxesInJobSlice: (
      state,
      { payload = { globalLineItemTaxes: [], globalServiceChargeTaxes: [] } }: PayloadAction<InvoiceGlobalTaxGroups>
    ) => {
      state.globalLineItemTaxes = payload.globalLineItemTaxes;
      state.globalServiceChargeTaxes = payload.globalServiceChargeTaxes;
    },
    updateLineItemsOrderInJobsSlice: (state, { payload }) => {
      state.jobLineItems = payload;
    },
    updateJobTitle: (state, { payload }: PayloadAction<string>) => {
      state.jobTitle = payload;
    },
    updateJobDescription: (state, { payload }: PayloadAction<string>) => {
      state.jobDescription = payload;
    },
    updateJobLeadSource: (state, { payload }: PayloadAction<string>) => {
      state.leadSource = payload;
    },
    updateJobLeadSourceType: (state, { payload }: PayloadAction<JobLeadSourceType>) => {
      state.leadSourceType = payload;
    },
    toggleIsJobScheduled: (state, { payload }: PayloadAction<boolean>) => {
      state.isJobScheduled = payload;
    },
    initialiseJobDetailsFromAPI: (state, { payload }: PayloadAction<Job>) => {
      state.estimateNumber = payload.estimates?.[0]?.estimateNumber;
      state.estimateStatus = payload.estimates?.[0]?.status as EstimateStatus;
      state.jobRelatedEntityData = {
        estimateId: payload.estimates?.[0]?.id,
        invoiceId: payload.invoices?.[0]?.id
      };
      state.invoiceNumberForJobFromInvoice = payload.invoices?.[0]?.invoiceNumber;
      state.initialAssigneeList = payload.assignees?.map(({ id, ...rest }) => ({
        merchantId: id,
        ...rest,
        email: '',
        phone: '',
        id,
        profilePictureUrl: rest.profilePictureURL || '',
        colorCode: ''
      }));
      state.assigneeList = payload.assignees?.map(({ id, ...rest }) => ({
        merchantId: id,
        ...rest,
        email: '',
        phone: '',
        id,
        profilePictureUrl: rest.profilePictureURL || '',
        colorCode: ''
      }));
      state.jobCustomer = {
        ...payload.customerLatest,
        ID: payload.customerLatest.id
      };
      state.initialJobCustomer = {
        ...payload.customerCurrent,
        ID: payload.customerCurrent.id
      };
      state.serviceAddress = payload.customerLatest.serviceAddress[0] as AddressField;
      state.initialServiceAddress = payload.customerCurrent.serviceAddress[0] as AddressField;
      state.billingAddress = payload.customerLatest.billingAddress as AddressField;
      state.initialBillingAddress = payload.customerCurrent.billingAddress as AddressField;
      state.jobTitle = payload.title;
      state.jobDescription = payload.description;
      state.leadSource = payload.leadSource;
      state.leadSourceType = LeadSourceOptions.find(leadSource => leadSource.id === payload.leadSourceType);
      state.isServiceFeeEnabled = payload.isServiceFeeEnabled;
      state.isServiceFeeEnabledInitial = payload.isServiceFeeEnabled;
      state.jobNotes = payload.note?.comment ?? '';
      state.jobReferenceNumber = payload.referenceNo;
      state.initialJobReferenceNumber = payload.referenceNo;
      state.isJobScheduled = payload.isScheduled;
      const currentDateTime = new Date();
      const oneHourLaterDateTime = new Date(currentDateTime.getTime() + 60 * 60 * 1000).toISOString();
      state.selectedDate = payload.isScheduled ? payload.scheduledFrom : currentDateTime.toISOString();
      state.scheduledFrom = payload.isScheduled ? payload.scheduledFrom : currentDateTime.toISOString();
      state.scheduledTo = payload.isScheduled ? payload.scheduledTo : oneHourLaterDateTime;
      if (payload.invoices?.length > 0) {
        state.invoiceStatus = payload.invoices[0].status;
      }
      state.externalCompany = payload.externalCompany ?? null;
      state.initialServiceArea = {
        serviceAreaId: payload.jobServiceArea?.id ?? '',
        serviceAreaName: payload.jobServiceArea?.name ?? '',
        isEnclosing: false,
        distance: 0
      };
      state.serviceArea = {
        serviceAreaId: payload.jobServiceArea?.id ?? '',
        serviceAreaName: payload.jobServiceArea?.name ?? '',
        isEnclosing: false,
        distance: 0
      };

      const parsedLineItems =
        payload.lineItems?.map(
          lineItem =>
            ({
              ...lineItem,
              itemDescription: lineItem.name,
              unitCost: lineItem.unitPrice,
              originalUnitCost: lineItem.unitCost ?? '',
              markupPercentage: lineItem.markupPercentage ?? '',
              quantity: lineItem.qty.toString(),
              total: +lineItem.unitPrice * +lineItem.qty,
              id: lineItem.id.toString(),
              taxes: parseLineItemTaxesFromResponse(lineItem.taxes)
            } as InvoiceLineItem)
        ) ?? [];
      const parsedServiceCharges = prepareInvoiceServiceCharge({
        serviceCharge: payload.serviceCharges?.at(0) as IInvoiceServiceChargeFromResponse
      });
      state.jobLineItems = parsedLineItems;
      state.initialJobLineItems = parsedLineItems;
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(parsedLineItems);
      state.lineItemsStateCount = payload.lineItems?.length || 0;
      state.appliedLineItemLevelTaxes = findAppliedUniqueTaxes({
        lineItems: parsedLineItems,
        serviceCharge: parsedServiceCharges
      });
      const lineItemsTotalCost = getLineItemsTotal(parsedLineItems);
      state.lineItemsTotal = lineItemsTotalCost;
      const discountData: DiscountFormData = {
        discountAmount: payload.discount,
        discountPercent: payload.discount,
        isDiscountAmountSelected: payload.discountType === DISCOUNT_TYPE.FLAT,
        discountInCurrency: payload.discount,
        amountAfterDiscount: null
      };

      const { subtotal: initialSubtotal } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: lineItemsTotalCost,
        discountData: discountData
      }) ?? { subtotal: lineItemsTotalCost, discountData: state.discountFormData };
      state.initialSubTotal = initialSubtotal;

      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: lineItemsTotalCost,
        discountData: state.discountSettings.enabled ? discountData : initialDiscountFormData
      }) ?? { subtotal: lineItemsTotalCost, discountData: state.discountFormData };
      state.jobSubTotal = subtotal;
      if (state.discountSettings.enabled) {
        state.discountFormData = newDiscountData;
      }
      if (!state.currentSurcharge.enabled) {
        state.isServiceFeeEnabled = false;
      } else if (
        state.currentSurcharge.config === FeatureToggleState.ALWAYS_ON ||
        state.currentSurcharge.config === FeatureToggleState.ON_BY_DEFAULT
      ) {
        state.isServiceFeeEnabled = true;
      } else {
        state.isServiceFeeEnabled = payload.isServiceFeeEnabled;
      }

      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(state.currentSurcharge.percentage, subtotal);

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurcharge.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.currentSurcharge = {
          percentage: state.currentSurcharge.percentage,
          valueInCurrency: surchargeInCurrency,
          enabled: state.currentSurcharge.enabled,
          basePoints: state.currentSurcharge.basePoints,
          config: state.currentSurcharge.config,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps
        };
        state.initialSurcharge = state.currentSurcharge;
      }

      // initialize service charge
      const serviceChargeInPayload = parsedServiceCharges;
      let updatedServiceCharge = serviceChargeInPayload;
      let serviceChargeBaseAmount = 0;
      // calculate total service charge base amount
      if (hasServiceCharge(serviceChargeInPayload)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: serviceChargeInPayload,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
        state.initialServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.currentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: parsedLineItems,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmountEditPage = calculateFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmount = finalAmountEditPage;

      state.jobAttachments = prepareInvoiceAttachments(payload.attachments as Attachment[]);

      state.selectedTags = payload.jobTags || [];

      // if (state.jobLineItems.length === 0) {
      //   state.discountFormData = initialDiscountFormData;
      // }
      state.isInitialisingSliceFromAPI = false;
    },
    initialiseJobDetailsFromJob: (state, { payload }: PayloadAction<Job>) => {
      state.invoiceStatus = '';
      state.assigneeList = payload.assignees?.map(({ id, ...rest }) => ({
        merchantId: id,
        ...rest,
        email: '',
        phone: '',
        id,
        profilePictureUrl: rest.profilePictureURL || '',
        colorCode: ''
      }));
      state.jobCustomer = {
        ...payload.customerLatest,
        ID: payload.customerLatest.id
      };
      state.initialJobCustomer = {
        ...payload.customerCurrent,
        ID: payload.customerCurrent.id
      };
      state.serviceAddress = payload.customerLatest.serviceAddress[0] as AddressField;
      state.initialServiceAddress = payload.customerCurrent.serviceAddress[0] as AddressField;
      state.billingAddress = payload.customerLatest.billingAddress as AddressField;
      state.initialBillingAddress = payload.customerCurrent.billingAddress as AddressField;
      state.jobTitle = payload.title;
      state.jobDescription = payload.description;
      state.leadSource = payload.leadSource;
      state.leadSourceType = LeadSourceOptions.find(leadSource => leadSource.id === payload.leadSourceType);
      state.isServiceFeeEnabled = payload.isServiceFeeEnabled;
      state.isServiceFeeEnabledInitial = payload.isServiceFeeEnabled;
      state.jobNotes = payload.note?.comment ?? '';
      state.jobReferenceNumber = payload.referenceNo;
      state.initialJobReferenceNumber = payload.referenceNo;
      state.isJobScheduled = payload.isScheduled;
      const currentDateTime = new Date();
      const oneHourLaterDateTime = new Date(currentDateTime.getTime() + 60 * 60 * 1000).toISOString();
      state.selectedDate = payload.isScheduled ? payload.scheduledFrom : currentDateTime.toISOString();
      state.scheduledFrom = payload.isScheduled ? payload.scheduledFrom : currentDateTime.toISOString();
      state.scheduledTo = payload.isScheduled ? payload.scheduledTo : oneHourLaterDateTime;

      state.externalCompany = payload.externalCompany ?? null;
      state.serviceArea = {
        serviceAreaId: payload.jobServiceArea?.id ?? '',
        serviceAreaName: payload.jobServiceArea?.name ?? '',
        isEnclosing: false,
        distance: 0
      };
      const parsedLineItems =
        payload.lineItems?.map(
          lineItem =>
            ({
              ...lineItem,
              itemDescription: lineItem.name,
              unitCost: lineItem.unitPrice,
              originalUnitCost: lineItem.unitCost ?? '',
              markupPercentage: lineItem.markupPercentage ?? '',
              quantity: lineItem.qty.toString(),
              total: +lineItem.unitPrice * +lineItem.qty,
              id: lineItem.id.toString(),
              taxes: parseLineItemTaxesFromResponse(lineItem.taxes)
            } as InvoiceLineItem)
        ) ?? [];
      const parsedServiceCharges = prepareInvoiceServiceCharge({
        serviceCharge: payload.serviceCharges?.at(0) as IInvoiceServiceChargeFromResponse
      });
      state.jobLineItems = parsedLineItems;
      state.initialJobLineItems = parsedLineItems;
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(parsedLineItems);
      state.lineItemsStateCount = payload.lineItems?.length || 0;
      state.appliedLineItemLevelTaxes = findAppliedUniqueTaxes({
        lineItems: parsedLineItems,
        serviceCharge: parsedServiceCharges
      });
      const lineItemsTotalCost = getLineItemsTotal(parsedLineItems);
      state.lineItemsTotal = lineItemsTotalCost;
      const discountData: DiscountFormData = {
        discountAmount: payload.discount,
        discountPercent: payload.discount,
        isDiscountAmountSelected: payload.discountType === DISCOUNT_TYPE.FLAT,
        discountInCurrency: payload.discount,
        amountAfterDiscount: null
      };

      const { subtotal: initialSubtotal } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: lineItemsTotalCost,
        discountData: discountData
      }) ?? { subtotal: lineItemsTotalCost, discountData: state.discountFormData };
      state.initialSubTotal = initialSubtotal;

      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: lineItemsTotalCost,
        discountData: state.discountSettings.enabled ? discountData : initialDiscountFormData
      }) ?? { subtotal: lineItemsTotalCost, discountData: state.discountFormData };
      state.jobSubTotal = subtotal;
      if (state.discountSettings.enabled) {
        state.discountFormData = newDiscountData;
      }
      if (!state.currentSurcharge.enabled) {
        state.isServiceFeeEnabled = false;
      } else if (state.currentSurcharge.config === FeatureToggleState.ALWAYS_ON) {
        state.isServiceFeeEnabled = true;
      } else state.isServiceFeeEnabled = payload.isServiceFeeEnabled;

      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(state.currentSurcharge.percentage, subtotal);

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurcharge.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.currentSurcharge = {
          percentage: state.currentSurcharge.percentage,
          valueInCurrency: surchargeInCurrency,
          enabled: state.currentSurcharge.enabled,
          basePoints: state.currentSurcharge.basePoints,
          config: state.currentSurcharge.config,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps
        };
        state.initialSurcharge = state.currentSurcharge;
      }

      // initialize service charge
      const serviceChargeInPayload = parsedServiceCharges;
      let updatedServiceCharge = serviceChargeInPayload;
      let serviceChargeBaseAmount = 0;
      // calculate total service charge base amount
      if (hasServiceCharge(serviceChargeInPayload)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: serviceChargeInPayload,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
        state.initialServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.currentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: parsedLineItems,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmountEditPage = calculateFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmount = finalAmountEditPage;

      state.jobAttachments = prepareInvoiceAttachments(payload.attachments as Attachment[]);

      state.selectedTags = payload.jobTags || [];

      state.isInitialisingSliceFromAPI = false;
    },
    initializeJobDetailsFromAutofill: (state, { payload }: PayloadAction<AutoFillJobDetailResponse['data']>) => {
      let autoFilledDataCount = 0;
      const isServiceAddressAvailable =
        payload.customerLatest.serviceAddress?.length > 0 &&
        Boolean(
          payload.customerLatest.serviceAddress?.[0]?.street ||
            payload.customerLatest.serviceAddress?.[0]?.apartment ||
            payload.customerLatest.serviceAddress?.[0]?.city ||
            payload.customerLatest.serviceAddress?.[0]?.state ||
            payload.customerLatest.serviceAddress?.[0]?.country ||
            payload.customerLatest.serviceAddress?.[0]?.zipCode
        );

      const isBillingAddressAvailable = Boolean(
        payload.customerLatest.billingAddress?.street ||
          payload.customerLatest.billingAddress?.apartment ||
          payload.customerLatest.billingAddress?.city ||
          payload.customerLatest.billingAddress?.state ||
          payload.customerLatest.billingAddress?.country ||
          payload.customerLatest.billingAddress?.zipCode
      );

      const isAnyCustomerData = Boolean(
        payload.customerLatest.id ||
          payload.customerLatest.name ||
          payload.customerLatest.lastName ||
          payload.customerLatest.email ||
          payload.customerLatest.phoneNumber ||
          isServiceAddressAvailable ||
          isBillingAddressAvailable
      );
      if (isAnyCustomerData) {
        autoFilledDataCount++;
        state.jobCustomer = {
          ...payload.customerLatest,
          ID: payload.customerLatest.id || '',
          teamID: payload.customerLatest.teamId || 0
        };
        state.serviceAddress = payload.customerLatest.serviceAddress?.[0] as AddressField;
        state.billingAddress = payload.customerLatest.billingAddress as AddressField;
      } else {
        state.jobCustomer = null;
        state.serviceAddress = null;
        state.billingAddress = null;
        state.serviceArea = null;
      }

      if (payload.title) {
        autoFilledDataCount++;
      }
      state.jobTitle = payload.title ?? '';

      if (payload.description) {
        autoFilledDataCount++;
      }
      state.jobDescription = payload.description ?? '';

      if (payload.leadSource || payload.leadSourceType) {
        autoFilledDataCount++;
      }
      state.leadSource = payload.leadSource ?? '';
      state.leadSourceType = LeadSourceOptions.find(leadSource => leadSource.id === payload.leadSourceType);

      if (payload.note?.comment) {
        autoFilledDataCount++;
      }
      state.jobNotes = payload.note?.comment ?? '';

      if (payload.referenceNo) {
        autoFilledDataCount++;
      }
      state.jobReferenceNumber = payload.referenceNo ?? '';

      if (payload.isScheduled) {
        autoFilledDataCount++;
      }
      state.isJobScheduled = payload.isScheduled;
      const currentDateTime = new Date();
      const oneHourLaterDateTime = new Date(currentDateTime.getTime() + 60 * 60 * 1000).toISOString();
      state.selectedDate = payload.isScheduled
        ? new Date(payload.scheduledFrom).toISOString()
        : currentDateTime.toISOString();
      state.scheduledFrom = payload.isScheduled
        ? new Date(payload.scheduledFrom).toISOString()
        : currentDateTime.toISOString();
      state.scheduledTo = payload.isScheduled ? new Date(payload.scheduledTo).toISOString() : oneHourLaterDateTime;

      state.externalCompany = payload.externalCompany ?? null;
      if (payload.attachments?.length > 0) {
        autoFilledDataCount++;
        state.jobAttachments = prepareInvoiceAttachments(payload.attachments as Attachment[]);
      } else {
        state.jobAttachments = [];
      }
      state.selectedTags = payload.jobTags || [];
      state.autofillKey = payload.autofillKey;
      state.autoFilledData = { ...payload, isAnyCustomerData };

      // Parse and initialize line items only when line items are available (same as invoice autofill)
      if (payload.lineItems?.length > 0) {
        autoFilledDataCount++;
        const parsedLineItems = payload.lineItems.map((lineItem, index) => {
          return {
            ...lineItem,
            itemDescription: lineItem.name,
            unitCost: lineItem.unitPrice,
            originalUnitCost: lineItem.unitCost ?? '',
            markupPercentage: lineItem.markupPercentage ?? '',
            quantity: lineItem.qty?.toString(),
            total: +lineItem.unitPrice * +lineItem.qty,
            id: lineItem.id?.toString() ?? index.toString(),
            taxes: parseLineItemTaxesFromResponse(lineItem.taxes)
          } as InvoiceLineItem;
        });

        const parsedServiceCharges = prepareInvoiceServiceCharge({
          serviceCharge: payload.serviceCharges?.at(0) as IInvoiceServiceChargeFromResponse
        });

        state.jobLineItems = parsedLineItems;
        state.initialJobLineItems = parsedLineItems;
        state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(parsedLineItems);
        state.lineItemsStateCount = payload.lineItems.length;

        // Calculate applied line item level taxes
        state.appliedLineItemLevelTaxes = findAppliedUniqueTaxes({
          lineItems: parsedLineItems,
          serviceCharge: parsedServiceCharges
        });

        // Calculate line items total
        const lineItemsTotalCost = getLineItemsTotal(parsedLineItems);
        state.lineItemsTotal = lineItemsTotalCost;

        // Initialize discount data
        const discountData: DiscountFormData = {
          discountAmount: payload.discount,
          discountPercent: payload.discount,
          isDiscountAmountSelected: payload.discountType === DISCOUNT_TYPE.FLAT,
          discountInCurrency: payload.discount,
          amountAfterDiscount: null
        };

        const { subtotal: initialSubtotal } = calculateInvoiceSubtotalAndNewDiscount({
          totalCostOfItems: lineItemsTotalCost,
          discountData: discountData
        }) ?? { subtotal: lineItemsTotalCost, discountData: state.discountFormData };
        state.initialSubTotal = initialSubtotal;

        const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
          totalCostOfItems: lineItemsTotalCost,
          discountData: state.discountSettings.enabled ? discountData : initialDiscountFormData
        }) ?? { subtotal: lineItemsTotalCost, discountData: state.discountFormData };

        // Update subtotal state
        state.jobSubTotal = subtotal;

        // Update discount data
        if (state.discountSettings.enabled) {
          state.discountFormData = newDiscountData;
        }

        // Initialize service fee enabled state
        if (!state.currentSurcharge.enabled) {
          state.isServiceFeeEnabled = false;
        } else if (
          state.currentSurcharge.config === FeatureToggleState.ALWAYS_ON ||
          state.currentSurcharge.config === FeatureToggleState.ON_BY_DEFAULT
        ) {
          state.isServiceFeeEnabled = true;
        } else {
          state.isServiceFeeEnabled = payload.isServiceFeeEnabled;
        }

        // Calculate surcharge
        let surchargeInCurrencyWithBps = 0;
        if (state.isServiceFeeEnabled) {
          const surchargeInCurrency: number = calculateSurchargeInCurrency(state.currentSurcharge.percentage, subtotal);

          surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
            surchargeInCurrency,
            state.currentSurcharge.basePoints
          );

          const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

          state.currentSurcharge = {
            percentage: state.currentSurcharge.percentage,
            valueInCurrency: surchargeInCurrency,
            enabled: state.currentSurcharge.enabled,
            basePoints: state.currentSurcharge.basePoints,
            config: state.currentSurcharge.config,
            percentageWithBps: surchargeWithBpsPercentage,
            valueInCurrencyWithBps: surchargeInCurrencyWithBps
          };
          state.initialSurcharge = state.currentSurcharge;
        }

        // Initialize service charge
        const serviceChargeInPayload = parsedServiceCharges;
        let updatedServiceCharge = serviceChargeInPayload;
        let serviceChargeBaseAmount = 0;

        // Calculate total service charge base amount
        if (hasServiceCharge(serviceChargeInPayload)) {
          const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
            updateServiceChargeWithBaseAmount({
              serviceCharge: serviceChargeInPayload,
              subtotal: subtotal
            });
          serviceChargeBaseAmount = baseAmount;
          updatedServiceCharge = updatedServiceChargeWithBaseAmount;
          state.selectedServiceCharge = updatedServiceCharge;
          state.initialServiceCharge = updatedServiceCharge;
        }

        // Update final amount
        const surchargeInCurrencyBpsValue = state.currentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

        // Update sum of line item level taxes
        const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
          lineItems: parsedLineItems,
          selectedServiceCharge: updatedServiceCharge
        });

        const finalAmount = calculateFinalAmount({
          subtotal,
          surcharge: surchargeInCurrencyBpsValue,
          totalServiceChargeInCurrency: serviceChargeBaseAmount,
          summatedLineItemLevelTaxesCurrency
        });
        state.finalAmount = finalAmount;
      } else {
        // No line items - reset to defaults
        state.jobLineItems = [];
        state.initialJobLineItems = [];
        state.lineItemsStateCount = 0;
        state.lineItemsTotal = 0;
        state.jobSubTotal = 0;
        state.initialSubTotal = 0;
        state.finalAmount = 0;
        state.totalOriginalUnitCost = 0;
        state.discountFormData = initialDiscountFormData;
        state.appliedLineItemLevelTaxes = [];
        state.selectedServiceCharge = initServiceCharge;
        // Initialize service fee enabled state
        if (!state.currentSurcharge.enabled) {
          state.isServiceFeeEnabled = false;
        } else if (
          state.currentSurcharge.config === FeatureToggleState.ALWAYS_ON ||
          state.currentSurcharge.config === FeatureToggleState.ON_BY_DEFAULT
        ) {
          state.isServiceFeeEnabled = true;
        } else {
          state.isServiceFeeEnabled = false;
        }
      }

      state.jobAutoFilledCount = autoFilledDataCount;
    },
    updateIsInitialisingJobFromAPI: (state, { payload }: PayloadAction<boolean>) => {
      state.isInitialisingSliceFromAPI = payload;
    },
    initialiseJobDetailsFromEstimate: (state, { payload }: PayloadAction<Estimate>) => {
      state.isFromEstimate = true;
      state.estimateId = payload.id;
      state.estimateNumber = payload.estimateNumber;
      state.estimateStatus = payload.status as EstimateStatus;
      state.invoiceNumberForJobFromInvoice = payload.invoices?.[0]?.invoiceNumber;
      state.invoiceStatus = payload.invoices?.[0]?.status ?? '';
      state.jobRelatedEntityData = {
        invoiceId: payload.invoices?.[0]?.id,
        estimateId: payload.id
      };
      state.jobCustomer = {
        ...payload.customer,
        ID: payload.customer.id,
        billingAddress: payload.customer['billingAddress'],
        teamID: payload.customer['teamID'],
        merchantId: payload.customer['merchantId'],
        createdBy: payload.customer['createdBy'],
        updatedBy: payload.customer['updatedBy']
      };
      state.serviceAddress = payload.customer?.serviceAddress?.[0] ?? null;
      state.initialServiceAddress = payload.customer?.serviceAddress?.[0] ?? null;
      state.billingAddress = payload.customer?.billingAddress ?? null;
      state.initialBillingAddress = payload.customer?.billingAddress ?? null;
      state.initialJobCustomer = { ...state.jobCustomer };

      // Initialize job title and description
      state.jobTitle = payload.title;
      state.jobDescription = payload.description;

      // Parse line items
      const parsedLineItems =
        payload.lineItems?.map(
          lineItem =>
            ({
              ...lineItem,
              itemDescription: lineItem.name,
              unitCost: lineItem.unitPrice,
              originalUnitCost: lineItem.unitCost ?? '',
              markupPercentage: lineItem.markupPercentage ?? '',
              quantity: lineItem.qty.toString(),
              total: +lineItem.unitPrice * +lineItem.qty,
              id: lineItem.id.toString(),
              taxes: parseLineItemTaxesFromResponse(lineItem.taxes || [])
            } as InvoiceLineItem)
        ) ?? [];

      // Update job line items
      state.jobLineItems = parsedLineItems;
      state.initialJobLineItems = parsedLineItems;
      state.lineItemsStateCount = parsedLineItems.length;

      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(parsedLineItems);

      // Parse service charges
      const parsedServiceCharges = prepareInvoiceServiceCharge({
        serviceCharge: payload?.serviceCharges?.[0] as unknown as IInvoiceServiceChargeFromResponse
      });

      // Calculate applied taxes
      state.appliedLineItemLevelTaxes = findAppliedUniqueTaxes({
        lineItems: parsedLineItems,
        serviceCharge: parsedServiceCharges
      });

      // Calculate totals
      const lineItemsTotalCost = getLineItemsTotal(parsedLineItems);
      state.lineItemsTotal = lineItemsTotalCost;

      // Initialize discount data
      const discountData: DiscountFormData = {
        discountAmount: payload.discount,
        discountPercent: payload.discount,
        isDiscountAmountSelected: payload.discountType === DISCOUNT_TYPE.FLAT,
        discountInCurrency: payload.discount,
        amountAfterDiscount: null
      };

      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: lineItemsTotalCost,
        discountData: state.discountSettings.enabled ? discountData : initialDiscountFormData
      }) ?? { subtotal: lineItemsTotalCost, discountData: state.discountFormData };
      state.jobSubTotal = subtotal;
      state.initialSubTotal = subtotal;

      if (state.discountSettings.enabled) {
        state.discountFormData = newDiscountData;
      }

      // Initialize service fee
      state.isServiceFeeEnabledInitial = payload.isServiceFeeEnabled;
      if (!state.currentSurcharge.enabled) {
        state.isServiceFeeEnabled = false;
      } else if (
        state.currentSurcharge.config === FeatureToggleState.ALWAYS_ON ||
        state.currentSurcharge.config === FeatureToggleState.ON_BY_DEFAULT
      ) {
        state.isServiceFeeEnabled = true;
      } else {
        state.isServiceFeeEnabled = payload.isServiceFeeEnabled;
      }

      // Calculate surcharge
      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(state.currentSurcharge.percentage, subtotal);
        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurcharge.basePoints
        );
        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.currentSurcharge = {
          ...state.currentSurcharge,
          valueInCurrency: surchargeInCurrency,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps
        };
        state.initialSurcharge = state.currentSurcharge;
      }

      // Initialize service charge
      let updatedServiceCharge = parsedServiceCharges;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
        state.initialServiceCharge = updatedServiceCharge;
      }

      // Calculate final amount
      const surchargeInCurrencyBpsValue = state.currentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: parsedLineItems,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmount = finalAmount;
      state.assigneeList =
        payload.assignees?.map(({ id, ...rest }) => ({
          merchantId: id,
          ...rest,
          email: '',
          phone: '',
          id,
          profilePictureUrl: rest.profilePictureURL || ''
        })) ?? [];
      state.isInitialisingSliceFromAPI = false;
    },

    // V2 API initialize reducer for jobs from invoice
    initialiseJobDetailsFromInvoiceV2: (state, { payload }: PayloadAction<InvoiceByIdResponseV2>) => {
      const invoiceData = payload.invoice;
      const customerData = payload.customer;

      state.jobRelatedEntityData = {
        invoiceId: invoiceData.invoiceId,
        estimateId: invoiceData.relatedEstimates?.[0]?.id
      };
      state.isJobFromInvoice = true;
      state.invoiceStatus = invoiceData.status;
      state.invoiceNumberForJobFromInvoice = invoiceData.invoiceNumber;

      // Prepare customer data using V2 transformer
      const preparedCustomer = prepareInvoiceCustomerFromV2Response(customerData);
      state.jobCustomer = preparedCustomer;
      state.initialJobCustomer = preparedCustomer;
      state.serviceAddress = (
        customerData.serviceAddress?.[0]?.id ? customerData.serviceAddress?.[0] : {}
      ) as CustomerAddress;
      state.initialServiceAddress = { ...state.serviceAddress } as CustomerAddress;
      state.billingAddress = (customerData.billingAddress as CustomerAddress) ?? null;
      state.initialBillingAddress = (customerData.billingAddress as CustomerAddress) ?? null;

      // Prepare line items using V2 transformer
      const preparedLineItems = prepareInvoiceLineItemsFromV2Response(invoiceData.lineItems);
      state.jobLineItems = preparedLineItems;
      state.initialJobLineItems = preparedLineItems;

      const totalLineItemsCost = getLineItemsTotal(preparedLineItems);
      state.lineItemsTotal = totalLineItemsCost;
      state.isServiceFeeEnabledInitial = invoiceData.serviceFee.enabled;
      state.lineItemsStateCount = invoiceData.lineItems?.length || 0;

      // Prepare service charge using V2 transformer
      const preparedServiceCharge = prepareInvoiceServiceChargeFromV2Response(
        invoiceData.serviceCharges?.[0]
      ) as IInvoiceServiceCharge;

      state.appliedLineItemLevelTaxes = findAppliedUniqueTaxes({
        lineItems: preparedLineItems,
        serviceCharge: preparedServiceCharge
      });

      // Prepare discount data using V2 transformer
      const discountFormData = prepareInvoiceDiscountFromV2Response(invoiceData.discount);

      const { subtotal: initialSubtotal } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: totalLineItemsCost,
        discountData: discountFormData
      }) ?? { subtotal: totalLineItemsCost, discountData: state.discountFormData };
      state.initialSubTotal = initialSubtotal;

      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: totalLineItemsCost,
        discountData: state.discountSettings.enabled ? discountFormData : initialDiscountFormData
      }) ?? { subtotal: totalLineItemsCost, discountData: state.discountFormData };

      // Update subtotal state
      state.jobSubTotal = subtotal;

      // Update discount data
      if (state.discountSettings.enabled) {
        state.discountFormData = newDiscountData;
      }

      state.isServiceFeeEnabledInitial = invoiceData.serviceFee.enabled;
      if (!state.currentSurcharge.enabled) {
        state.isServiceFeeEnabled = false;
      } else if (
        state.currentSurcharge.config === FeatureToggleState.ALWAYS_ON ||
        state.currentSurcharge.config === FeatureToggleState.ON_BY_DEFAULT
      ) {
        state.isServiceFeeEnabled = true;
      } else state.isServiceFeeEnabled = invoiceData.serviceFee.enabled;

      // Surcharge calculation
      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabled) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(state.currentSurcharge.percentage, subtotal);

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurcharge.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.currentSurcharge = {
          percentage: state.currentSurcharge.percentage,
          valueInCurrency: surchargeInCurrency,
          enabled: state.currentSurcharge.enabled,
          basePoints: state.currentSurcharge.basePoints,
          config: state.currentSurcharge.config,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps
        };

        state.initialSurcharge = { ...state.currentSurcharge } as InvoiceSurchargeState;
      }

      // Initialize service charge
      let updatedServiceCharge = preparedServiceCharge;
      let serviceChargeBaseAmount = 0;

      if (hasServiceCharge(preparedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: preparedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
        state.initialServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.currentSurcharge.enabled ? surchargeInCurrencyWithBps : 0;

      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: preparedLineItems,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmount = finalAmount;

      // Calculate total original unit cost
      state.totalOriginalUnitCost = calculateTotalOriginalUnitCost(preparedLineItems);

      state.isInitialisingSliceFromAPI = false;
    },

    updateExternalCompany: (state, { payload }: PayloadAction<JobExternalCompany>) => {
      state.externalCompany = payload;
    },
    resetAutoFilledData: state => {
      state.autoFilledData = null;
    },
    setAutoFilledData: (state, { payload }: PayloadAction<AutoFillJobDetailResponse['data']>) => {
      state.autoFilledData = payload;
    },
    updateServiceArea: (state, { payload }: PayloadAction<ServiceAreaInfo>) => {
      state.serviceArea = payload;
    },
    updateSelectedTags: (state, { payload }: PayloadAction<Tag[]>) => {
      state.selectedTags = payload;
    },
    updateServiceAreaAssignees: (state, { payload }: PayloadAction<AssignableMember[]>) => {
      state.serviceAreaAssignees = payload;
    },
    updateAssignableTeamMembers: (state, { payload }: PayloadAction<AssignableMember[]>) => {
      state.assignableTeamMembers = payload;
    }
  }
});

export const {
  resetCreateJobState,
  updateServiceAddress,
  updateJobCustomer,
  updateBillingAddress,
  updateJobNotes,
  updateJobAttachments,
  removeJobAttachments,
  modifyJobAttachment,
  updateMultipleJobAttachments,
  updateJobReferenceNumber,
  updateAssigneeList,
  updateSelectedDate,
  updateScheduledFrom,
  updateScheduledTo,
  updateIsBackFromCreateTaxInJobsSlice,
  updateJobDraftData,
  resetJobDraftData,
  addCreatedTaxInInvoiceDraft,
  addJobValidationError,
  removeJobValidationErrors,
  resetJobValidationErrors,
  addJobLineItem,
  incrementJobLineItemQty,
  addGlobalTaxesInJobSlice,
  removeJobDiscountFormData,
  updateJobDiscountFormData,
  updateInvoiceFeeSettingsInJobs,
  updateIsServiceFeeEnabledInJobs,
  removeJobServiceCharge,
  updateSelectedJobServiceCharge,
  updateLineItemsOrderInJobsSlice,
  modifyJobLineItem,
  removeJobLineItem,
  updateJobDescription,
  updateJobLeadSource,
  updateJobLeadSourceType,
  updateJobTitle,
  toggleIsJobScheduled,
  initializeJobDetailsFromAutofill,
  initialiseJobDetailsFromAPI,
  updateIsInitialisingJobFromAPI,
  initialiseJobDetailsFromEstimate,
  initialiseJobDetailsFromInvoiceV2,
  resetJobAttachmentState,
  updateExternalCompany,
  resetAutoFilledData,
  setAutoFilledData,
  initialiseJobDetailsFromJob,
  updateServiceArea,
  updateSelectedTags,
  updateServiceAreaAssignees,
  updateAssignableTeamMembers
} = createJobSlice.actions;

export const selectServiceAddress = (state: RootState) => state.rootReducer.createJob.serviceAddress;
export const selectBillingAddress = (state: RootState) => state.rootReducer.createJob.billingAddress;
export const selectJobCustomer = (state: RootState) => state.rootReducer.createJob.jobCustomer;
export const selectJobNotes = (state: RootState) => state.rootReducer.createJob.jobNotes;
export const selectJobAttachments = (state: RootState) => state.rootReducer.createJob.jobAttachments;
export const selectJobReferenceNumber = (state: RootState) => state.rootReducer.createJob.jobReferenceNumber;
export const selectInitialJobReferenceNumber = (state: RootState) =>
  state.rootReducer.createJob.initialJobReferenceNumber;
export const selectAssigneeList = (state: RootState) => state.rootReducer.createJob.assigneeList;
export const selectInitialAssigneeList = (state: RootState) => state.rootReducer.createJob.initialAssigneeList;
export const selectSelectedDate = (state: RootState) => state.rootReducer.createJob.selectedDate;
export const selectScheduledFrom = (state: RootState) => state.rootReducer.createJob.scheduledFrom;
export const selectScheduledTo = (state: RootState) => state.rootReducer.createJob.scheduledTo;
export const selectIsBackFromCreateTaxFromJobsSlice = (state: RootState) =>
  state.rootReducer.createJob.isBackFromCreateTaxRoute;
export const selectJobLineItemDraftModalData = (state: RootState) => state.rootReducer.createJob.jobDraftModalData;
export const selectJobValidationErrors = (state: RootState) => state.rootReducer.createJob.jobValidationErrors;
export const selectCurrentSurchargeInJobs = (state: RootState) => state.rootReducer.createJob.currentSurcharge;
export const selectJobLineItems = (state: RootState) => state.rootReducer.createJob.jobLineItems;
export const selectJobDiscountSettings = (state: RootState) => state.rootReducer.createJob.discountSettings;
export const selectJobSelectedServiceCharge = (state: RootState) => state.rootReducer.createJob.selectedServiceCharge;
export const selectJobLineItemsTotal = (state: RootState) => state.rootReducer.createJob.lineItemsTotal;
export const selectJobDiscountFormData = (state: RootState) => state.rootReducer.createJob.discountFormData;
export const selectJobFinalAmount = (state: RootState) => state.rootReducer.createJob.finalAmount;
export const selectJobSubtotal = (state: RootState) => state.rootReducer.createJob.jobSubTotal;
export const selectAppliedJobLineItemLevelTaxes = (state: RootState) =>
  state.rootReducer.createJob.appliedLineItemLevelTaxes;
export const selectGlobalLineItemTaxesJobsSlice = (state: RootState) => state.rootReducer.createJob.globalLineItemTaxes;
export const selectIsServiceFeeEnabledFromJob = (state: RootState) => state.rootReducer.createJob.isServiceFeeEnabled;
export const selectGlobalServiceChargeTaxesInJobSlice = (state: RootState) =>
  state.rootReducer.createJob.globalServiceChargeTaxes;
export const selectJobTitle = (state: RootState) => state.rootReducer.createJob.jobTitle;
export const selectJobDescription = (state: RootState) => state.rootReducer.createJob.jobDescription;
export const selectJobLeadSource = (state: RootState) => state.rootReducer.createJob.leadSource;
export const selectJobLeadSourceType = (state: RootState) => state.rootReducer.createJob.leadSourceType;
export const selectCurrentJob = (state: RootState) => state.rootReducer.createJob;
export const selectIsInitialisingJobFromAPI = (state: RootState) =>
  state.rootReducer.createJob.isInitialisingSliceFromAPI;
export const selectIsJobScheduled = (state: RootState) => state.rootReducer.createJob.isJobScheduled;
export const selectIsJobFromInvoice = (state: RootState) => state.rootReducer.createJob.isJobFromInvoice;
export const selectInvoiceNumberForJobFromInvoice = (state: RootState) =>
  state.rootReducer.createJob.invoiceNumberForJobFromInvoice;
export const selectJobInvoiceStatus = (state: RootState) => state.rootReducer.createJob.invoiceStatus;
export const selectIsFromEstimate = (state: RootState) => state.rootReducer.createJob.isFromEstimate;
export const selectEstimateStatus = (state: RootState) => state.rootReducer.createJob.estimateStatus;
export const selectEstimateNumberFromJob = (state: RootState) => state.rootReducer.createJob.estimateNumber;
export const selectJobRelatedEntityData = (state: RootState) => state.rootReducer.createJob.jobRelatedEntityData;
export const selectExternalCompany = (state: RootState) => state.rootReducer.createJob.externalCompany;
export const selectAutoFilledData = (state: RootState) => state.rootReducer.createJob.autoFilledData;
export const selectTotalOriginalUnitCost = (state: RootState) => state.rootReducer.createJob.totalOriginalUnitCost;
export const selectInitialServiceArea = (state: RootState) => state.rootReducer.createJob.initialServiceArea;
export const selectServiceArea = (state: RootState) => state.rootReducer.createJob.serviceArea;
export const selectServiceAreaAssignees = (state: RootState) => state.rootReducer.createJob.serviceAreaAssignees;
export const selectAssignableTeamMembers = (state: RootState) => state.rootReducer.createJob.assignableTeamMembers;
export const selectSelectedTags = (state: RootState) => state.rootReducer.createJob.selectedTags;
export const selectAutoFilledDataCount = (state: RootState) => state.rootReducer.createJob.jobAutoFilledCount;
export default createJobSlice.reducer;
